import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle, ArrowLeft, Ban, CalendarDays, Check, Clock, LogIn, LogOut, Minus, Moon, Plus,
  ShieldCheck, Tag, Users, Loader2, CreditCard, Store,
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import api, { formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { payForBooking } from "../lib/razorpay";
import AirbnbLink from "../components/AirbnbLink";
import {
  errorMessage, fmtDay, newIdempotencyKey, saveBookingToken, toISODate,
} from "../lib/stays";

// classNames passed directly into react-day-picker so the grid layout wins over shadcn defaults
const LUXE_CAL = {
  months: "w-full",
  month: "w-full",
  caption: "relative flex items-center justify-center pb-4 mb-3 luxe-cal-caption",
  caption_label: "font-display text-xl text-white tracking-wide",
  nav_button: "luxe-cal-nav-btn w-9 h-9 flex items-center justify-center transition-all opacity-100 bg-transparent p-0",
  nav_button_previous: "absolute left-0",
  nav_button_next: "absolute right-0",
  table: "w-full border-collapse",
  head_row: "grid grid-cols-7 w-full",
  head_cell: "luxe-cal-head text-center py-2",
  row: "grid grid-cols-7 w-full mt-[3px]",
  cell: "p-[2px]",
  day: "luxe-cal-day w-full h-11 sm:h-12 flex items-center justify-center rounded-[2px] cursor-pointer border border-transparent transition-all",
  day_selected: "luxe-cal-selected",
  day_today: "luxe-cal-today",
  day_outside: "opacity-0 pointer-events-none",
  day_disabled: "opacity-20 line-through cursor-not-allowed",
  day_range_start: "luxe-cal-selected",
  day_range_end: "luxe-cal-selected",
  day_range_middle: "luxe-cal-range-mid rounded-none",
  day_hidden: "invisible",
};

const STEPS = ["Dates & guests", "Review & rules", "Your details"];
const HOURS_OF_DAY = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);
const label12 = (hhmm) => {
  const h = Number(hhmm.slice(0, 2));
  return `${h % 12 || 12}:00 ${h < 12 ? "AM" : "PM"}`;
};
const ymd = toISODate;
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

function Counter({ label, hint, value, min = 0, max, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
      <div>
        <div className="text-white text-sm">{label}</div>
        {hint && <div className="text-xs text-white/40">{hint}</div>}
      </div>
      <div className="flex items-center gap-3">
        <button type="button" aria-label={`Decrease ${label}`} disabled={value <= min}
          onClick={() => onChange(value - 1)}
          className="w-9 h-9 border border-white/15 flex items-center justify-center text-white/70 disabled:opacity-30 hover:border-gold transition-colors">
          <Minus size={14} />
        </button>
        <span className="w-6 text-center text-white" aria-live="polite" data-testid={testid}>{value}</span>
        <button type="button" aria-label={`Increase ${label}`} disabled={value >= max}
          onClick={() => onChange(value + 1)}
          className="w-9 h-9 border border-white/15 flex items-center justify-center text-white/70 disabled:opacity-30 hover:border-gold transition-colors">
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
}

function TimingPills({ t }) {
  if (!t) return null;
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="timing-pills">
      <div className="border border-white/10 p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40"><LogIn size={12} className="text-gold" /> Check-in</div>
        <div className="text-white mt-1">From {t.check_in_label}</div>
      </div>
      <div className="border border-white/10 p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40"><LogOut size={12} className="text-gold" /> Check-out</div>
        <div className="text-white mt-1">Before {t.check_out_label}</div>
      </div>
    </div>
  );
}

function Check4({ id, checked, onChange, children, tone, testid }) {
  return (
    <label htmlFor={id} className={`flex items-start gap-3 p-4 border cursor-pointer ${tone === "red" ? "border-red-500/40 bg-red-500/[0.04]" : "border-white/10"}`}>
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-[#C9A24B]" data-testid={testid} />
      <span className="text-sm text-white/80">{children}</span>
    </label>
  );
}

function PriceLines({ pricing }) {
  if (!pricing) return null;
  return (
    <div className="space-y-2 text-sm" data-testid="price-breakdown">
      {pricing.line_items.map((l) => (
        <div key={l.key} className="flex justify-between gap-4">
          <span className="text-white/60">{l.label}</span>
          <span className={l.amount.startsWith("-") ? "text-green-400" : "text-white"}>
            {l.amount.startsWith("-") ? "−" + formatINR(l.amount.slice(1)) : formatINR(l.amount)}
          </span>
        </div>
      ))}
      <div className="flex justify-between border-t border-white/10 pt-3 mt-3 font-display text-xl">
        <span>Total</span><span className="text-gold" data-testid="price-total">{formatINR(pricing.total_amount)}</span>
      </div>
    </div>
  );
}

export default function BookingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [prop, setProp] = useState(null);
  const [propError, setPropError] = useState(false);
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(params.get("type") === "hourly" ? "hourly" : "overnight");

  // stay selection
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [hourDate, setHourDate] = useState(undefined);
  const [startTime, setStartTime] = useState("19:00");
  const [hours, setHours] = useState(3);
  const [early, setEarly] = useState(false);
  const [late, setLate] = useState(false);

  // guests
  const [guests, setGuests] = useState({ adults: 2, children: 0, infants: 0, pets: 0 });

  // availability calendar
  const [dayStatus, setDayStatus] = useState({});
  const [calLoading, setCalLoading] = useState(true);
  const [month, setMonth] = useState(startOfToday());

  // extras / coupon / price
  const [addonList, setAddonList] = useState([]);
  const [addons, setAddons] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteState, setQuoteState] = useState("idle"); // idle | loading | ok | unavailable | error
  const [quoteMsg, setQuoteMsg] = useState("");

  // acceptances + contact
  const [acc, setAcc] = useState({ rules: false, cancellation: false, timings: false, nonRefundable: false });
  const [contact, setContact] = useState({ name: "", email: "", phone: "", requests: "" });
  const [payOnline, setPayOnline] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const keyRef = useRef({ sig: "", key: "" });

  useEffect(() => {
    api.get("/properties").then((r) => {
      const p = r.data[0];
      if (!p) { setPropError(true); return; }
      setProp(p);
      setHours(Math.max(p.min_hours, 3));
      if (!p.allow_hourly) setMode("overnight");
    }).catch(() => setPropError(true));
    api.get("/room/addons").then((r) => setAddonList(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setContact((c) => ({ ...c, name: c.name || user.name || "", email: c.email || user.email || "", phone: c.phone || user.phone || "" }));
  }, [user]);

  // Availability is always fetched fresh (server sends no-store); refetch when the visible month changes.
  const loadDays = useCallback(async (from) => {
    if (!prop) return;
    setCalLoading(true);
    try {
      const first = new Date(from.getFullYear(), from.getMonth(), 1);
      const last = new Date(first); last.setDate(last.getDate() + 100);
      const { data } = await api.get(`/properties/${prop.id}/availability`, { params: { from: ymd(first), to: ymd(last) } });
      setDayStatus((prev) => {
        const next = { ...prev };
        data.days.forEach((d) => { next[d.date] = d; });
        return next;
      });
    } catch { /* calendar degrades gracefully; the server validates anyway */ }
    setCalLoading(false);
  }, [prop]);

  useEffect(() => { loadDays(month); }, [loadDays, month]);

  // ---- derived stay ----
  const nightFree = useCallback((d) => {
    const s = dayStatus[ymd(d)];
    return !s || s.status === "available" || s.status === "partial";
  }, [dayStatus]);

  const isDisabled = useCallback((d) => {
    if (d < startOfToday()) return true;
    if (mode === "hourly") return dayStatus[ymd(d)]?.status === "blocked";
    // picking the check-out: a day is selectable if every night before it is free (even if another stay starts that day)
    if (range.from && !range.to && d > range.from) {
      const cur = new Date(range.from);
      while (cur < d) { if (!nightFree(cur)) return true; cur.setDate(cur.getDate() + 1); }
      return false;
    }
    return !nightFree(d);
  }, [mode, range, dayStatus, nightFree]);

  const nights = range.from && range.to ? Math.round((range.to - range.from) / 86400000) : 0;
  const stayReady = mode === "overnight" ? nights >= 1 : !!hourDate;

  const stayPayload = useMemo(() => {
    const base = { adults: guests.adults, children: guests.children, infants: guests.infants, pets: guests.pets };
    if (mode === "overnight") {
      if (!(range.from && range.to)) return null;
      return { ...base, booking_type: "overnight", check_in_date: ymd(range.from), check_out_date: ymd(range.to), early_check_in: early, late_check_out: late };
    }
    if (!hourDate) return null;
    return { ...base, booking_type: "hourly", date: ymd(hourDate), start_time: startTime, hours };
  }, [mode, range, hourDate, startTime, hours, guests, early, late]);

  // ---- server quote (debounced; aborted when inputs change) ----
  const quoteSig = JSON.stringify([stayPayload, addons, coupon]);
  useEffect(() => {
    if (!prop || !stayPayload) { setQuote(null); setQuoteState("idle"); return undefined; }
    const ctrl = new AbortController();
    setQuoteState("loading");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/properties/${prop.id}/quote`, { ...stayPayload, addon_ids: addons, coupon_code: coupon || undefined }, { signal: ctrl.signal });
        setQuote(data);
        setQuoteState(data.available ? "ok" : "unavailable");
        setQuoteMsg(data.available ? "" : data.message);
      } catch (e) {
        if (ctrl.signal.aborted || e.code === "ERR_CANCELED") return;
        setQuote(null);
        setQuoteState("error");
        setQuoteMsg(errorMessage(e, "We couldn't check availability right now. Please try again."));
      }
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop, quoteSig]);

  const policy = quote?.cancellation || prop?.cancellation;
  const timings = quote?.timings || prop?.timings;
  const nonRefundable = policy?.refund_type === "NON_REFUNDABLE";
  const payMode = prop?.payment?.mode || "OFF";               // OFF | OPTIONAL | REQUIRED (server decides; re-checked on submit)
  const willPayOnline = payMode === "REQUIRED" || (payMode === "OPTIONAL" && payOnline);

  const goReview = () => {
    if (!stayPayload) { toast.error("Please select your dates first"); return; }
    if (quoteState === "loading") { toast.message("Checking availability…"); return; }
    if (quoteState !== "ok") { toast.error(quoteMsg || "Please choose different dates."); return; }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const acceptedAll = acc.rules && acc.cancellation && acc.timings && (!nonRefundable || acc.nonRefundable);
  const detailsOk = contact.name.trim().length > 1 && /\S+@\S+\.\S+/.test(contact.email) && contact.phone.replace(/\D/g, "").length >= 7;

  const applyCoupon = () => { setCoupon(couponInput.trim().toUpperCase()); };

  const confirm = async () => {
    if (submitting) return;
    setSubmitError(""); setFieldErrors({});
    if (!acceptedAll) { setStep(2); toast.error("Please accept the rules and policies to continue"); return; }
    if (!detailsOk) { toast.error("Please complete your contact details"); return; }
    const body = {
      ...stayPayload, property_id: prop.id, addon_ids: addons, coupon_code: coupon || undefined,
      guest_name: contact.name, guest_email: contact.email, guest_phone: contact.phone, special_requests: contact.requests,
      rules_accepted: acc.rules, cancellation_policy_accepted: acc.cancellation, timings_accepted: acc.timings,
      non_refundable_accepted: nonRefundable ? acc.nonRefundable : false,
      pay_online: payMode === "OPTIONAL" ? payOnline : undefined,
    };
    // One key per distinct submission: double-clicks and network retries replay the original booking.
    const sig = JSON.stringify(body);
    if (keyRef.current.sig !== sig) keyRef.current = { sig, key: newIdempotencyKey() };
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/", body, { headers: { "Idempotency-Key": keyRef.current.key } });
      saveBookingToken(data.id, data.access_token);
      if (data.online_payment_due) {
        // Dates are now held for a few minutes while the guest pays.
        const result = await payForBooking(data.id, data.access_token);
        if (result.status === "failed" && result.message) toast.error(result.message);
        if (result.status === "dismissed") toast.message("Payment not completed. Your dates are held for a few minutes — you can finish paying from your booking page.");
        navigate(`/booking/confirmation/${data.id}`, { state: { token: data.access_token } });
        return;
      }
      navigate(`/booking/confirmation/${data.id}`, { state: { booking: data, token: data.access_token } });
    } catch (e) {
      const d = e.response?.data;
      setSubmitError(errorMessage(e));
      if (d?.fields) setFieldErrors(d.fields);
      if (d?.code === "unavailable") { setQuoteState("unavailable"); loadDays(month); }
      setSubmitting(false);
    }
  };

  const selectMode = (m) => {
    setMode(m); setStep(1); setQuote(null); setQuoteState("idle");
  };

  const summaryStay = mode === "overnight"
    ? (range.from && range.to ? `${fmtDay(ymd(range.from), { day: "numeric", month: "short" })} → ${fmtDay(ymd(range.to), { day: "numeric", month: "short" })} · ${nights} night${nights !== 1 ? "s" : ""}` : "Select your dates")
    : (hourDate ? `${fmtDay(ymd(hourDate), { day: "numeric", month: "short" })} · ${label12(startTime)} · ${hours} h` : "Select a date");

  if (propError) {
    return (
      <div className="pt-40 pb-24 min-h-screen text-center" data-testid="booking-page">
        <AlertCircle className="mx-auto text-gold mb-4" />
        <p className="text-white/60">Bookings are unavailable right now. Please try again shortly or contact us.</p>
      </div>
    );
  }
  if (!prop) {
    return (
      <div className="pt-32 pb-24 min-h-screen" data-testid="booking-page" aria-busy="true">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 glass h-[520px] animate-pulse" /><div className="glass h-80 animate-pulse" />
        </div>
      </div>
    );
  }

  const cta = step === 1
    ? { text: quoteState === "loading" ? "Checking…" : "Check availability", onClick: goReview, disabled: !stayReady || quoteState === "loading" }
    : step === 2
      ? { text: "Continue", onClick: () => { setStep(3); window.scrollTo({ top: 0, behavior: "smooth" }); }, disabled: !acceptedAll }
      : { text: submitting ? "Please wait…" : payMode === "QR" ? "Reserve & get payment QR" : willPayOnline && quote?.pricing ? `Pay ${formatINR(quote.pricing.total_amount)} & confirm` : "Confirm booking", onClick: confirm, disabled: submitting || !detailsOk };

  return (
    <div className="min-h-screen grain pb-28 lg:pb-24" style={{ background: "var(--bg-base)" }} data-testid="booking-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-12 pt-28 sm:pt-32">
        <div className="mb-8">
          <div className="eyebrow">Reserve</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-3">{prop.name}</h1>
          <p className="text-white/50 text-sm mt-2">{[prop.city, prop.address].filter(Boolean).join(" · ")}</p>
        </div>

        {/* Stepper */}
        <ol className="flex items-center gap-2 sm:gap-4 mb-10 text-xs uppercase tracking-[0.15em]" aria-label="Booking progress">
          {STEPS.map((s, i) => {
            const n = i + 1; const done = step > n; const active = step === n;
            return (
              <li key={s} className="flex items-center gap-2 sm:gap-4 min-w-0">
                <button type="button" disabled={!done} onClick={() => setStep(n)} aria-current={active ? "step" : undefined}
                  className={`flex items-center gap-2 ${active ? "text-gold" : done ? "text-white/70 hover:text-gold" : "text-white/30"}`}>
                  <span className={`w-6 h-6 border flex items-center justify-center shrink-0 ${active ? "border-gold" : "border-white/20"}`}>
                    {done ? <Check size={12} /> : n}
                  </span>
                  <span className="hidden sm:inline truncate">{s}</span>
                </button>
                {n < STEPS.length && <span className="w-6 sm:w-12 h-px bg-white/10" />}
              </li>
            );
          })}
        </ol>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-2 space-y-8">
            {/* ───────── STEP 1 ───────── */}
            {step === 1 && (
              <>
                {prop.allow_hourly && (
                  <div className="grid grid-cols-2 gap-3" role="tablist" aria-label="Stay type">
                    {[["overnight", "Overnight stay", Moon], ["hourly", "Hourly stay", Clock]].map(([v, text, Icon]) => (
                      <button key={v} type="button" role="tab" aria-selected={mode === v} onClick={() => selectMode(v)}
                        className={`p-4 border flex items-center justify-center gap-2 text-sm transition-colors ${mode === v ? "border-gold text-gold bg-gold/[0.06]" : "border-white/10 text-white/60 hover:border-white/30"}`}
                        data-testid={`mode-${v}`}>
                        <Icon size={15} /> {text}
                      </button>
                    ))}
                  </div>
                )}

                <section className="glass p-5 sm:p-7" aria-labelledby="dates-h">
                  <h2 id="dates-h" className="font-display text-xl mb-1 flex items-center gap-2"><CalendarDays size={18} className="text-gold" />
                    {mode === "overnight" ? "Select check-in and check-out" : "Select your date"}</h2>
                  <p className="text-xs text-white/40 mb-5">
                    {mode === "overnight"
                      ? `Minimum ${prop.min_stay_nights} night${prop.min_stay_nights !== 1 ? "s" : ""}. You can check in on the day another guest checks out.`
                      : `${prop.min_hours}–${prop.max_hours} hours per stay.`}
                  </p>
                  <div className="relative" aria-busy={calLoading}>
                    {mode === "overnight" ? (
                      <Calendar mode="range" selected={range} month={month} onMonthChange={setMonth}
                        onSelect={(r, day) => {
                          // restart selection if a full range already exists
                          if (range.from && range.to) { setRange({ from: day, to: undefined }); return; }
                          setRange(r || { from: undefined, to: undefined });
                        }}
                        numberOfMonths={1} disabled={isDisabled} showOutsideDays={false}
                        className="luxe-calendar" classNames={LUXE_CAL} />
                    ) : (
                      <Calendar mode="single" selected={hourDate} month={month} onMonthChange={setMonth} onSelect={setHourDate}
                        disabled={isDisabled} showOutsideDays={false} className="luxe-calendar" classNames={LUXE_CAL} />
                    )}
                    {calLoading && <div className="absolute inset-0 bg-black/30 flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>}
                  </div>
                  <div className="flex flex-wrap gap-4 text-[11px] text-white/40 mt-4">
                    <span className="flex items-center gap-2"><i className="w-3 h-3 bg-gold inline-block" /> Selected</span>
                    <span className="flex items-center gap-2"><i className="w-3 h-3 border border-white/20 inline-block opacity-30" /> Unavailable</span>
                  </div>
                  {mode === "overnight" && (range.from || range.to) && (
                    <button type="button" className="text-xs text-gold mt-4 underline" onClick={() => setRange({ from: undefined, to: undefined })}>Clear dates</button>
                  )}

                  {mode === "hourly" && (
                    <div className="grid sm:grid-cols-2 gap-4 mt-6">
                      <div>
                        <Label htmlFor="start-time" className="text-[10px] uppercase tracking-[0.25em] text-white/50">Start time</Label>
                        <select id="start-time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                          className="mt-2 w-full bg-[#0f0f0f] border border-white/10 h-12 px-3 text-white text-sm">
                          {HOURS_OF_DAY.map((t) => <option key={t} value={t}>{label12(t)}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="hours" className="text-[10px] uppercase tracking-[0.25em] text-white/50">Duration</Label>
                        <select id="hours" value={hours} onChange={(e) => setHours(Number(e.target.value))}
                          className="mt-2 w-full bg-[#0f0f0f] border border-white/10 h-12 px-3 text-white text-sm">
                          {Array.from({ length: prop.max_hours - prop.min_hours + 1 }, (_, i) => prop.min_hours + i)
                            .map((h) => <option key={h} value={h}>{h} hours</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </section>

                <section className="glass p-5 sm:p-7" aria-labelledby="guests-h">
                  <h2 id="guests-h" className="font-display text-xl mb-2 flex items-center gap-2"><Users size={18} className="text-gold" /> Guests</h2>
                  <Counter label="Adults" hint="Age 18+" value={guests.adults} min={1} max={Math.min(prop.max_adults, prop.max_guests - guests.children)}
                    onChange={(v) => setGuests({ ...guests, adults: v })} testid="adults-count" />
                  {prop.allow_children && <Counter label="Children" hint="Under 18" value={guests.children} max={prop.max_guests - guests.adults}
                    onChange={(v) => setGuests({ ...guests, children: v })} testid="children-count" />}
                  {prop.allow_infants && <Counter label="Infants" hint="Not counted toward capacity" value={guests.infants} max={prop.max_infants}
                    onChange={(v) => setGuests({ ...guests, infants: v })} />}
                  {prop.allow_pets && <Counter label="Pets" value={guests.pets} max={prop.max_pets} onChange={(v) => setGuests({ ...guests, pets: v })} />}
                  <p className="text-xs text-white/40 mt-3">Maximum {prop.max_guests} guests.</p>

                  {mode === "overnight" && (prop.timings.early_checkin_enabled || prop.timings.late_checkout_enabled) && (
                    <div className="mt-6 space-y-3">
                      {prop.timings.early_checkin_enabled && (
                        <Check4 id="early" checked={early} onChange={setEarly}>
                          Early check-in from {prop.timings.early_checkin_label}{Number(prop.timings.early_checkin_fee) > 0 && ` (+${formatINR(prop.timings.early_checkin_fee)})`}
                        </Check4>)}
                      {prop.timings.late_checkout_enabled && (
                        <Check4 id="late" checked={late} onChange={setLate}>
                          Late check-out until {prop.timings.late_checkout_label}{Number(prop.timings.late_checkout_fee) > 0 && ` (+${formatINR(prop.timings.late_checkout_fee)})`}
                        </Check4>)}
                    </div>
                  )}
                </section>

                <TimingPills t={prop.timings} />

                {quoteState === "unavailable" && (
                  <div role="alert" className="flex gap-3 p-4 border border-red-500/40 bg-red-500/[0.05] text-sm text-red-200" data-testid="unavailable-alert">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" /> {quoteMsg}
                  </div>
                )}
                {quoteState === "error" && (
                  <div role="alert" className="flex gap-3 p-4 border border-yellow-500/40 text-sm text-yellow-100"><AlertCircle size={16} className="shrink-0 mt-0.5" /> {quoteMsg}</div>
                )}
              </>
            )}

            {/* ───────── STEP 2 ───────── */}
            {step === 2 && quote && (
              <>
                <button type="button" onClick={() => setStep(1)} className="flex items-center gap-2 text-sm text-white/50 hover:text-gold"><ArrowLeft size={14} /> Change dates or guests</button>

                <section className="glass p-5 sm:p-7" aria-labelledby="stay-h">
                  <h2 id="stay-h" className="font-display text-xl mb-4">Your stay</h2>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm mb-5">
                    <div><div className="text-white/40 text-xs uppercase tracking-[0.2em]">Dates</div><div className="text-white mt-1">{summaryStay}</div></div>
                    <div><div className="text-white/40 text-xs uppercase tracking-[0.2em]">Guests</div>
                      <div className="text-white mt-1">{guests.adults} adult{guests.adults !== 1 ? "s" : ""}{guests.children ? `, ${guests.children} child${guests.children !== 1 ? "ren" : ""}` : ""}</div></div>
                  </div>
                  <TimingPills t={timings} />
                </section>

                {addonList.length > 0 && (
                  <section className="glass p-5 sm:p-7">
                    <h2 className="font-display text-xl mb-4">Optional extras</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {addonList.map((a) => (
                        <Check4 key={a.id} id={`addon-${a.id}`} checked={addons.includes(a.id)}
                          onChange={(v) => setAddons((x) => v ? [...x, a.id] : x.filter((i) => i !== a.id))}>
                          <span className="text-white">{a.emoji} {a.name}</span> <span className="text-gold">{formatINR(a.price)}</span>
                          {a.description && <span className="block text-xs text-white/40">{a.description}</span>}
                        </Check4>
                      ))}
                    </div>
                  </section>
                )}

                <section className="glass p-5 sm:p-7" aria-labelledby="rules-h">
                  <h2 id="rules-h" className="font-display text-xl mb-4 flex items-center gap-2"><ShieldCheck size={18} className="text-gold" /> Booking rules</h2>
                  <div className={`p-4 border mb-5 ${nonRefundable ? "border-red-500/40 bg-red-500/[0.05]" : "border-white/10"}`} data-testid="policy-box">
                    <div className="flex items-center gap-2 text-white">
                      {nonRefundable && <Ban size={15} className="text-red-400" />}
                      <b>{nonRefundable ? "Non-refundable booking" : policy?.name}</b>
                    </div>
                    {nonRefundable ? (
                      <p className="text-sm text-red-200/90 mt-2">{policy.non_refundable_notice}</p>
                    ) : (
                      <ul className="list-disc pl-5 mt-2 text-sm text-white/60 space-y-1">{policy?.lines.map((l) => <li key={l}>{l}</li>)}</ul>
                    )}
                  </div>
                  <div className="space-y-3">
                    <Check4 id="acc-rules" checked={acc.rules} onChange={(v) => setAcc({ ...acc, rules: v })} testid="accept-rules">
                      I agree to the <a href="/rules" target="_blank" rel="noreferrer" className="text-gold underline">property rules</a>.
                    </Check4>
                    <Check4 id="acc-cancel" checked={acc.cancellation} onChange={(v) => setAcc({ ...acc, cancellation: v })} testid="accept-cancellation">
                      I agree to the cancellation policy.
                    </Check4>
                    <Check4 id="acc-times" checked={acc.timings} onChange={(v) => setAcc({ ...acc, timings: v })} testid="accept-timings">
                      I understand the check-in ({timings?.check_in_label}) and check-out ({timings?.check_out_label}) times.
                    </Check4>
                    {nonRefundable && (
                      <Check4 id="acc-nr" tone="red" checked={acc.nonRefundable} onChange={(v) => setAcc({ ...acc, nonRefundable: v })} testid="accept-non-refundable">
                        I understand that this booking is non-refundable and cannot be refunded after confirmation.
                      </Check4>
                    )}
                  </div>
                </section>
              </>
            )}

            {/* ───────── STEP 3 ───────── */}
            {step === 3 && (
              <>
                <button type="button" onClick={() => setStep(2)} className="flex items-center gap-2 text-sm text-white/50 hover:text-gold"><ArrowLeft size={14} /> Back to review</button>
                <section className="glass p-5 sm:p-7" aria-labelledby="details-h">
                  <h2 id="details-h" className="font-display text-xl mb-5">Guest information</h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-2">
                      <Label htmlFor="g-name" className="text-[10px] uppercase tracking-[0.25em] text-white/50">Full name</Label>
                      <Input id="g-name" autoComplete="name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                        aria-invalid={!!fieldErrors.guest_name} className="mt-2 bg-[#0f0f0f] border-white/10 h-12" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="g-email" className="text-[10px] uppercase tracking-[0.25em] text-white/50">Email</Label>
                      <Input id="g-email" type="email" autoComplete="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                        aria-invalid={!!fieldErrors.guest_email} className="mt-2 bg-[#0f0f0f] border-white/10 h-12" data-testid="contact-email-input" />
                    </div>
                    <div>
                      <Label htmlFor="g-phone" className="text-[10px] uppercase tracking-[0.25em] text-white/50">Phone</Label>
                      <Input id="g-phone" type="tel" autoComplete="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                        aria-invalid={!!fieldErrors.guest_phone} className="mt-2 bg-[#0f0f0f] border-white/10 h-12" data-testid="contact-phone-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="g-req" className="text-[10px] uppercase tracking-[0.25em] text-white/50">Special requests (optional)</Label>
                      <Textarea id="g-req" maxLength={1000} value={contact.requests} onChange={(e) => setContact({ ...contact, requests: e.target.value })}
                        className="mt-2 bg-[#0f0f0f] border-white/10 min-h-[100px]" />
                    </div>
                  </div>
                </section>

                <section className="glass p-5 sm:p-7" aria-labelledby="pay-h" data-testid="payment-section">
                  <h2 id="pay-h" className="font-display text-xl mb-4 flex items-center gap-2"><CreditCard size={18} className="text-gold" /> Payment</h2>
                  {payMode === "OPTIONAL" && (
                    <div className="grid sm:grid-cols-2 gap-3 mb-4" role="radiogroup" aria-label="Payment method">
                      {[[true, "Pay now online", "UPI, cards, net banking", CreditCard], [false, "Pay at the property", "No online payment needed", Store]].map(([v, t, sub, Icon]) => (
                        <button key={String(v)} type="button" role="radio" aria-checked={payOnline === v} onClick={() => setPayOnline(v)}
                          className={`text-left p-4 border ${payOnline === v ? "border-gold bg-gold/[0.06]" : "border-white/10 hover:border-white/30"}`}>
                          <div className="flex items-center gap-2 text-white"><Icon size={14} className="text-gold" /> {t}</div>
                          <div className="text-xs text-white/40 mt-1">{sub}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {payMode === "QR" ? (
                    <div className="text-sm text-white/70 space-y-3" data-testid="pay-qr-note">
                      <p>After you reserve, you'll see our <b className="text-gold">payment QR code</b>. Your dates are held for about {prop?.payment?.qr_hold_minutes || 60} minutes.</p>
                      <ol className="list-decimal pl-5 space-y-1 text-white/60">
                        <li>Scan the QR and pay <b className="text-gold">{quote?.pricing ? formatINR(quote.pricing.total_amount) : "the total"}</b>.</li>
                        <li>Send the payment screenshot to us on WhatsApp.</li>
                        <li>We verify it and confirm your booking.</li>
                      </ol>
                    </div>
                  ) : willPayOnline ? (
                    <p className="text-sm text-white/70" data-testid="pay-online-note">
                      You'll pay <b className="text-gold">{quote?.pricing ? formatINR(quote.pricing.total_amount) : "the total"}</b> securely with Razorpay (UPI, cards, net banking, wallets).
                      Your dates are held for {prop?.payment?.hold_minutes || 15} minutes while you pay, and your booking is confirmed as soon as the payment succeeds.
                    </p>
                  ) : (
                    <p className="text-sm text-white/70">Payment is collected at the property — you won't be charged online.</p>
                  )}
                  <p className="text-xs text-white/40 mt-4">Both guests must carry valid photo ID.</p>
                </section>
                {submitError && (
                  <div role="alert" className="flex gap-3 p-4 border border-red-500/40 bg-red-500/[0.05] text-sm text-red-200" data-testid="submit-error">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" /> {submitError}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ───────── Sticky summary ───────── */}
          <aside className="lg:sticky lg:top-28" aria-label="Booking summary">
            <div className="glass p-6 border border-gold/15" data-testid="booking-summary">
              <div className="font-display text-xl">{prop.name}</div>
              <div className="text-sm text-white/50 mt-1" data-testid="summary-stay">{summaryStay}</div>
              <div className="my-5 h-px bg-white/10" />
              {quoteState === "loading" && <div className="space-y-2" aria-busy="true">{[...Array(4)].map((_, i) => <div key={i} className="h-4 bg-white/10 animate-pulse" />)}</div>}
              {quote?.pricing && quoteState !== "loading" && (
                <>
                  <PriceLines pricing={quote.pricing} />
                  <div className="mt-5 pt-5 border-t border-white/10" data-testid="coupon-box">
                    <label htmlFor="coupon" className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/50"><Tag size={12} className="text-gold" /> Have a coupon?</label>
                    {coupon && quote.pricing.coupon_code ? (
                      <div className="flex items-center justify-between mt-2 text-sm text-green-400">
                        <span>{quote.pricing.coupon_code} applied</span>
                        <button type="button" className="text-xs text-white/50 underline" onClick={() => { setCoupon(""); setCouponInput(""); }}>Remove</button>
                      </div>
                    ) : (
                      <form className="flex gap-2 mt-2" onSubmit={(e) => { e.preventDefault(); applyCoupon(); }}>
                        <Input id="coupon" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Enter code"
                          className="bg-[#0f0f0f] border-white/10 h-10 text-sm" />
                        <button type="submit" disabled={!couponInput.trim()} className="btn-ghost !px-4 !py-2 shrink-0 disabled:opacity-40">Apply</button>
                      </form>
                    )}
                  </div>
                  <div className="mt-5 text-xs text-white/50 flex items-center gap-2">
                    {nonRefundable ? <><Ban size={12} className="text-red-400" /> Non-refundable</> : <><Check size={12} className="text-green-400" /> {policy?.name}</>}
                  </div>
                </>
              )}
              {!quote && quoteState === "idle" && <p className="text-sm text-white/40">Select dates to see the price.</p>}
              {coupon && quoteState === "error" && (
                <div role="alert" className="mt-4 text-xs text-red-300">
                  {quoteMsg}{" "}<button type="button" className="underline" onClick={() => { setCoupon(""); setCouponInput(""); }}>Remove coupon</button>
                </div>
              )}
              <div className="hidden lg:block mt-6">
                <button type="button" onClick={cta.onClick} disabled={cta.disabled} className="btn-gold w-full disabled:opacity-40" data-testid="primary-cta">{cta.text}</button>
              </div>
              {prop.airbnb_url && (
                <div className="mt-4 pt-4 border-t border-white/10 text-center">
                  <div className="text-[11px] text-white/40 mb-2">Prefer to book through Airbnb?</div>
                  <AirbnbLink url={prop.airbnb_url} className="btn-ghost w-full"
                    stay={{ checkIn: mode === "overnight" && range.from && range.to ? ymd(range.from) : "", checkOut: mode === "overnight" && range.from && range.to ? ymd(range.to) : "", ...guests }} />
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile bottom CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[#0b0908]/95 backdrop-blur px-5 py-3 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-gold font-display text-lg" data-testid="mobile-total">{quote?.pricing ? formatINR(quote.pricing.total_amount) : "—"}</div>
          <div className="text-[11px] text-white/40 truncate">{summaryStay}</div>
        </div>
        <button type="button" onClick={cta.onClick} disabled={cta.disabled} className="btn-gold shrink-0 disabled:opacity-40" data-testid="mobile-cta">{cta.text}</button>
      </div>
    </div>
  );
}
